import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiBellRingingBold,
    PiCheckCircleFill,
    PiClockCountdownBold,
    PiMagnifyingGlassBold,
    PiSpinnerGapBold,
    PiTimerBold,
    PiTruckBold,
    PiWarningBold,
    PiXBold,
} from 'react-icons/pi';
import { PageHeader, Skeleton, Switch } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type Option = { value: string; label: string };

type TemplateRow = {
    id: number;
    number?: number;
    title: string;
    sort_order: number;
    sector: string | null;
    sector_color: string | null;
    is_auto: boolean;
    sla_days: number | null;
    sla_business_days: boolean;
    sla_start: string;
    sla_start_field: string | null;
    sla_notify_emails: string[] | null;
};

type HubUser = { name: string; email: string };

type Response = {
    stages: { value: string; label: string; templates: TemplateRow[] }[];
    options: { starts: Option[]; fields: Option[]; users: HubUser[] };
    stats: { templates: number; with_sla: number; open_shipments: number; overdue_tasks: number };
};

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

// Setor guarda o tom do tema (primary, success…), não o hex.
const SECTOR_TONE: Record<string, string> = {
    primary: '#C9A84C',
    warning: '#F59E0B',
    info: '#0EA5E9',
    danger: '#EF4444',
    success: '#10B981',
    gray: '#9CA3AF',
    rose: '#F43F5E',
    secondary: '#6B7280',
};
const sectorColor = (c: string | null) => (!c ? '#9CA3AF' : c.startsWith('#') ? c : (SECTOR_TONE[c] ?? '#9CA3AF'));

const inputCls =
    'h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm outline-none focus:border-primary dark:border-gray-600 dark:bg-gray-900';

function Stat({ icon, value, label, tone }: { icon: ReactNode; value: ReactNode; label: string; tone: string }) {
    return (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/60">
            <span className={cn('flex size-9 items-center justify-center rounded-lg text-lg', tone)}>{icon}</span>
            <span>
                <span className="block text-lg font-bold tabular-nums leading-none text-gray-900 dark:text-gray-100">{value}</span>
                <span className="mt-1 block text-xs text-gray-500">{label}</span>
            </span>
        </div>
    );
}

function TemplateSlaRow({ row, options, onSaved }: { row: TemplateRow; options: Response['options']; onSaved: (row: TemplateRow, applied: number) => void }) {
    const [days, setDays] = useState(row.sla_days ? String(row.sla_days) : '');
    const [business, setBusiness] = useState(row.sla_business_days);
    const [start, setStart] = useState(row.sla_start || 'previous_task');
    const [field, setField] = useState(row.sla_start_field ?? '');
    const [emails, setEmails] = useState<string[]>(row.sla_notify_emails ?? []);
    const [draft, setDraft] = useState('');
    const [state, setState] = useState<SaveState>('idle');
    const [applied, setApplied] = useState<number | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const first = useRef(true);

    const invalidDays = days !== '' && (!/^\d+$/.test(days) || Number(days) < 1 || Number(days) > 365);
    const missingField = start === 'shipment_field' && !field;

    useEffect(() => {
        if (first.current) {
            first.current = false;
            return;
        }
        if (invalidDays || missingField) return;
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setState('saving');
            try {
                const { data } = await api.put<{ data: TemplateRow; applied: number }>(`/settings/task-sla/${row.id}`, {
                    sla_days: days === '' ? null : Number(days),
                    sla_business_days: business,
                    sla_start: start,
                    sla_start_field: start === 'shipment_field' ? field : null,
                    sla_notify_emails: emails,
                    apply_to_open: true,
                });
                setState('saved');
                setApplied(data.applied);
                onSaved(data.data, data.applied);
            } catch (e) {
                setState('error');
                toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Não foi possível salvar o prazo.');
            }
        }, 700);
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days, business, start, field, emails]);

    const nameOf = (email: string) => options.users.find((u) => u.email === email)?.name;
    const addEmail = (raw: string) => {
        const list = raw
            .split(/[\s,;]+/)
            .map((e) => e.trim().toLowerCase())
            .filter(Boolean);
        const valid = list.filter((e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
        if (valid.length < list.length) toast.error('E-mail inválido: ' + list.filter((e) => !valid.includes(e)).join(', '));
        if (valid.length) setEmails((prev) => Array.from(new Set([...prev, ...valid])));
        setDraft('');
    };

    const hasSla = days !== '' && !invalidDays;

    return (
        <div className={cn('grid items-center gap-x-3 gap-y-2 px-4 py-3 @3xl:grid-cols-[minmax(0,1fr)_7.5rem_7rem_minmax(0,15rem)_6rem]', !hasSla && 'bg-gray-50/60 dark:bg-gray-900/30')}>
            <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    <span className="mr-1.5 font-mono text-xs text-gray-400">{String(row.number ?? row.sort_order).padStart(2, '0')}</span>
                    {row.title}
                </p>
                <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    {row.sector ? (
                        <span
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                            style={{
                                color: sectorColor(row.sector_color),
                                backgroundColor: `${sectorColor(row.sector_color)}1A`,
                                boxShadow: `inset 0 0 0 1px ${sectorColor(row.sector_color)}40`,
                            }}
                        >
                            <span className="size-1.5 rounded-full" style={{ backgroundColor: sectorColor(row.sector_color) }} />
                            {row.sector}
                        </span>
                    ) : (
                        'Sem setor'
                    )}
                    {row.is_auto && <span className="rounded bg-gray-100 px-1 text-[10px] dark:bg-gray-700">automática</span>}
                </p>
            </div>

            <label className="flex items-center gap-1.5">
                <input
                    inputMode="numeric"
                    value={days}
                    onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
                    placeholder="—"
                    aria-label={`Prazo de ${row.title}`}
                    className={cn(inputCls, 'w-16 text-center tabular-nums', invalidDays && 'border-rose-400')}
                />
                <span className="text-xs text-gray-500">dias</span>
            </label>

            <div className={cn('flex items-center gap-2', !hasSla && 'opacity-40')}>
                <Switch
                    size="sm"
                    checked={business}
                    disabled={!hasSla}
                    onChange={(e) => setBusiness(e.target.checked)}
                    aria-label="Contar só dias úteis"
                    className="shrink-0 [&_label]:my-0"
                />
                <span className="text-xs text-gray-600 dark:text-gray-300">{business ? 'úteis' : 'corridos'}</span>
            </div>

            <div className={cn('flex min-w-0 flex-col gap-1.5', !hasSla && 'opacity-40')}>
                <select value={start} disabled={!hasSla} onChange={(e) => setStart(e.target.value)} aria-label="Começa a contar em" className={cn(inputCls, 'w-full')}>
                    {options.starts.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                {start === 'shipment_field' && (
                    <select
                        value={field}
                        disabled={!hasSla}
                        onChange={(e) => setField(e.target.value)}
                        aria-label="Data do processo"
                        className={cn(inputCls, 'w-full', missingField && 'border-rose-400')}
                    >
                        <option value="">Escolha a data…</option>
                        {options.fields.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {hasSla && (
                <div className="flex flex-wrap items-center gap-1.5 @3xl:col-span-5 @3xl:pl-6">
                    <span className="text-[11px] font-semibold text-gray-500">Avisar também:</span>
                    {emails.map((e) => (
                        <span
                            key={e}
                            title={e}
                            className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-inset ring-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-800"
                        >
                            {nameOf(e) ?? e}
                            <button
                                type="button"
                                onClick={() => setEmails((prev) => prev.filter((x) => x !== e))}
                                aria-label={`Remover ${e}`}
                                className="text-violet-400 hover:text-violet-700"
                            >
                                <PiXBold className="size-3" />
                            </button>
                        </span>
                    ))}
                    <input
                        list={`sla-users-${row.id}`}
                        value={draft}
                        onChange={(e) => {
                            const v = e.target.value;
                            // Escolheu uma sugestão da lista: entra direto.
                            if (options.users.some((u) => u.email === v.trim().toLowerCase())) addEmail(v);
                            else setDraft(v);
                        }}
                        onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ',' || e.key === ';') && draft.trim()) {
                                e.preventDefault();
                                addEmail(draft);
                            }
                        }}
                        onBlur={() => draft.trim() && addEmail(draft)}
                        placeholder={emails.length ? '+ e-mail' : 'e-mail de quem deve ser avisado se atrasar'}
                        aria-label={`Avisar também em ${row.title}`}
                        className="h-7 min-w-[14rem] flex-1 rounded-md border border-dashed border-gray-300 bg-transparent px-2 text-xs outline-none focus:border-primary dark:border-gray-600"
                    />
                    <datalist id={`sla-users-${row.id}`}>
                        {options.users
                            .filter((u) => !emails.includes(u.email))
                            .map((u) => (
                                <option key={u.email} value={u.email}>
                                    {u.name}
                                </option>
                            ))}
                    </datalist>
                </div>
            )}

            <div className="text-right text-[11px] @3xl:col-start-5 @3xl:row-start-1">
                {state === 'saving' && (
                    <span className="inline-flex items-center gap-1 text-gray-500">
                        <PiSpinnerGapBold className="animate-spin" /> salvando
                    </span>
                )}
                {state === 'saved' && (
                    <span className="inline-flex flex-col items-end text-emerald-600">
                        <span className="inline-flex items-center gap-1 font-semibold">
                            <PiCheckCircleFill /> salvo
                        </span>
                        {applied !== null && applied > 0 && <span className="text-gray-400">em {applied} processo(s)</span>}
                    </span>
                )}
                {state === 'error' && <span className="font-semibold text-rose-600">erro</span>}
                {state === 'idle' && !hasSla && <span className="text-gray-400">sem prazo</span>}
            </div>
        </div>
    );
}

export default function TaskSlaSettingsPage() {
    const qc = useQueryClient();
    const [search, setSearch] = useState('');
    const [onlyMissing, setOnlyMissing] = useState(false);

    const query = useQuery({
        queryKey: ['settings', 'task-sla'],
        queryFn: async () => (await api.get<Response>('/settings/task-sla')).data,
    });

    const [withSla, setWithSla] = useState<Record<number, boolean>>({});
    useEffect(() => {
        if (query.data) {
            setWithSla(Object.fromEntries(query.data.stages.flatMap((s) => s.templates.map((t) => [t.id, !!t.sla_days]))));
        }
    }, [query.data]);

    const stages = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (query.data?.stages ?? [])
            .map((s) => ({
                ...s,
                templates: s.templates.filter(
                    (t) =>
                        (!term || `${t.title} ${t.sector ?? ''}`.toLowerCase().includes(term)) && (!onlyMissing || !withSla[t.id]),
                ),
            }))
            .filter((s) => s.templates.length > 0);
    }, [query.data, search, onlyMissing, withSla]);

    const configured = Object.values(withSla).filter(Boolean).length;
    const stats = query.data?.stats;

    return (
        <div className="@container">
            <PageHeader
                title="Prazos das tarefas (SLA)"
                breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Configurações' }, { name: 'Prazos das tarefas' }]}
            />

            <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
                Defina em quantos dias cada tarefa do processo de importação deve ser feita e a partir de quando o prazo começa. A tarefa
                ganha vencimento em todos os processos abertos (o que já foi concluído não muda) e quem passa do prazo entra no{' '}
                <strong>resumo diário de tarefas atrasadas</strong> (dias úteis, 07:30). Em cada tarefa, <strong>“Avisar também”</strong>{' '}
                recebe e-mails extras só daquela tarefa (vale mesmo de fora do Hub). Quem recebe todas as tarefas é configurado em{' '}
                <Link to="/settings/email-notifications" className="font-semibold text-primary hover:underline">
                    Notificações por e-mail
                </Link>
                .
            </div>

            {query.isLoading ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 @3xl:grid-cols-4">
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-16 rounded-xl" />
                        ))}
                    </div>
                    <Skeleton className="h-96 rounded-2xl" />
                </div>
            ) : query.isError || !query.data ? (
                <p className="text-sm text-rose-600">Não foi possível carregar os prazos (é preciso ser admin ou poder editar modelos de tarefa).</p>
            ) : (
                <>
                    <div className="mb-5 grid grid-cols-2 gap-3 @3xl:grid-cols-4">
                        <Stat
                            icon={<PiTimerBold />}
                            value={`${configured} de ${stats?.templates ?? 0}`}
                            label="tarefas com prazo"
                            tone="bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300"
                        />
                        <Stat
                            icon={<PiTruckBold />}
                            value={stats?.open_shipments ?? 0}
                            label="processos em andamento"
                            tone="bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-300"
                        />
                        <Stat
                            icon={<PiWarningBold />}
                            value={stats?.overdue_tasks ?? 0}
                            label="tarefas atrasadas agora"
                            tone={
                                (stats?.overdue_tasks ?? 0) > 0
                                    ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700'
                            }
                        />
                        <Link
                            to="/settings/email-notifications"
                            className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 transition hover:border-primary dark:border-gray-700 dark:bg-gray-800/60"
                        >
                            <span className="flex size-9 items-center justify-center rounded-lg bg-violet-50 text-lg text-violet-600 dark:bg-violet-950/40 dark:text-violet-300">
                                <PiBellRingingBold />
                            </span>
                            <span className="text-xs">
                                <span className="block font-semibold text-gray-900 dark:text-gray-100">Quem é avisado</span>
                                <span className="text-gray-500">Notificações → “Tarefas atrasadas e vencendo”</span>
                            </span>
                        </Link>
                    </div>

                    <div className="mb-3 flex flex-wrap items-center gap-3">
                        <label className="relative">
                            <PiMagnifyingGlassBold className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar tarefa ou setor"
                                className={cn(inputCls, 'w-64 pl-8')}
                            />
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <Switch
                                size="sm"
                                checked={onlyMissing}
                                onChange={(e) => setOnlyMissing(e.target.checked)}
                                aria-label="Só tarefas sem prazo"
                                className="shrink-0 [&_label]:my-0"
                            />
                            Só tarefas sem prazo
                        </label>
                    </div>

                    <div className="space-y-4">
                        {stages.map((stage) => (
                            <section key={stage.value} className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60">
                                <header className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900/40">
                                    <PiClockCountdownBold className="text-gray-400" />
                                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{stage.label}</h2>
                                    <span className="text-xs text-gray-400">
                                        {stage.templates.filter((t) => withSla[t.id]).length}/{stage.templates.length} com prazo
                                    </span>
                                </header>
                                <div className="hidden gap-x-3 border-b border-gray-100 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-gray-700 @3xl:grid @3xl:grid-cols-[minmax(0,1fr)_7.5rem_7rem_minmax(0,15rem)_6rem]">
                                    <span>Tarefa</span>
                                    <span>Prazo</span>
                                    <span>Dias</span>
                                    <span>Começa a contar em</span>
                                    <span />
                                </div>
                                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {stage.templates.map((t) => (
                                        <TemplateSlaRow
                                            key={t.id}
                                            row={t}
                                            options={query.data.options}
                                            onSaved={(saved) => {
                                                setWithSla((prev) => ({ ...prev, [saved.id]: !!saved.sla_days }));
                                                qc.invalidateQueries({ queryKey: ['shipment-tasks'] });
                                            }}
                                        />
                                    ))}
                                </div>
                            </section>
                        ))}
                        {stages.length === 0 && <p className="py-10 text-center text-sm text-gray-500">Nenhuma tarefa encontrada.</p>}
                    </div>
                </>
            )}
        </div>
    );
}
